import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Button, Heading, Text } from "@nbs/ui";
import { downloadCsv } from "@/features/analytics/utils/download-csv";
import {
  formatAvailabilityLabel,
  formatIndicatorStatusLabel,
  formatQualityLabel,
} from "@/features/analytics/utils/domain-labels";
import { NbsKpiCard } from "@/features/charts/NbsKpiCard";
import {
  NbsCatalogFilterBar,
  type CatalogFilterField,
} from "@/features/filters/NbsCatalogFilterBar";
import { INDICATORS_CATALOG_QUERY } from "../api/documents";
import { useIndicatorFilters } from "../hooks/useIndicatorFilters";

const PAGE_SIZE = 40;

export function IndicatorsCatalog() {
  const { state, setState, reset, graphqlFilters, activeCount } = useIndicatorFilters();
  const { data, loading, error, refetch } = useQuery(INDICATORS_CATALOG_QUERY, {
    variables: {
      filters: graphqlFilters,
      first: PAGE_SIZE,
      offset: state.page * PAGE_SIZE,
      sort: state.sort,
    },
    fetchPolicy: "cache-and-network",
  });

  const summary = data?.indicatorSummary;
  const options = data?.indicatorFilterOptions;
  const page = data?.indicatorCatalog;
  const nodes = page?.nodes ?? [];
  const totalPages = Math.max(1, Math.ceil((page?.totalCount ?? 0) / PAGE_SIZE));

  const pageState = error
    ? "error"
    : loading && !data
      ? "loading"
      : !summary
        ? "empty"
        : activeCount > 0 && nodes.length === 0
          ? "filtered-empty"
          : "populated";

  const filterFields: CatalogFilterField[] = [
    {
      kind: "search",
      key: "search",
      label: "Recherche",
      inputId: "ind-q",
      value: state.search,
      placeholder: "Libellé, code, service, unité…",
      onChange: (search) => setState({ search, page: 0 }),
    },
    {
      kind: "multi",
      key: "services",
      label: "Service",
      selectId: "ind-service",
      value: state.serviceIds,
      options: (options?.services ?? []).map((s: { id: string; libelle: string }) => ({
        value: s.id,
        label: s.libelle,
      })),
      onChange: (serviceIds) => setState({ serviceIds, page: 0 }),
    },
    {
      kind: "multi",
      key: "categories",
      label: "Catégorie",
      selectId: "ind-cat",
      value: state.categories,
      options: (options?.categories ?? []).map((c: string) => ({ value: c, label: c })),
      onChange: (categories) => setState({ categories, page: 0 }),
    },
    {
      kind: "multi",
      key: "availability",
      label: "Disponibilité",
      selectId: "ind-avail",
      value: state.dataAvailability,
      options: (options?.dataAvailabilities ?? []).map((a: string) => ({
        value: a,
        label: formatAvailabilityLabel(a),
      })),
      onChange: (dataAvailability) => setState({ dataAvailability, page: 0 }),
    },
    {
      kind: "multi",
      key: "units",
      label: "Unité",
      selectId: "ind-unit",
      value: state.units,
      options: (options?.units ?? []).map((u: string) => ({ value: u, label: u })),
      onChange: (units) => setState({ units, page: 0 }),
    },
    {
      kind: "multi",
      key: "frequencies",
      label: "Fréquence",
      selectId: "ind-freq",
      value: state.frequencies,
      options: (options?.frequencies ?? []).map((f: string) => ({ value: f, label: f })),
      onChange: (frequencies) => setState({ frequencies, page: 0 }),
    },
    {
      kind: "multi",
      key: "territories",
      label: "Territoire",
      selectId: "ind-terr",
      value: state.territoryIds,
      options: (options?.territories ?? []).map(
        (t: { id: string; label: string; kind: string }) => ({
          value: t.id,
          label: `${t.label} (${t.kind})`,
        }),
      ),
      onChange: (territoryIds) => setState({ territoryIds, page: 0 }),
    },
    {
      kind: "multi",
      key: "statuses",
      label: "Statut",
      selectId: "ind-status",
      value: state.statuses,
      options: (options?.statuses ?? []).map((s: string) => ({
        value: s,
        label: formatIndicatorStatusLabel(s),
      })),
      onChange: (statuses) => setState({ statuses, page: 0 }),
    },
    {
      kind: "single",
      key: "sort",
      label: "Tri",
      selectId: "ind-sort",
      value: state.sort,
      options: [
        { value: "libelle_asc", label: "Libellé A→Z" },
        { value: "libelle_desc", label: "Libellé Z→A" },
        { value: "measureCount_desc", label: "Mesures ↓" },
        { value: "measureCount_asc", label: "Mesures ↑" },
      ],
      onChange: (sort) => setState({ sort, page: 0 }),
    },
  ];

  return (
    <article className="nbs-catalog" data-testid="indicators-catalog" data-state={pageState}>
      <header className="nbs-catalog__header">
        <div>
          <Heading level={1}>Indicateurs</Heading>
          <Text tone="muted">
            Catalogue complet des indicateurs Écosystèmes Guinée, y compris ceux sans mesure
            historique. Point d’entrée vers l’analyse N2 et le registre des mesures.
          </Text>
        </div>
        <div className="nbs-catalog__header-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              downloadCsv("indicateurs.csv", [
                [
                  "id",
                  "libelle",
                  "service",
                  "categorie",
                  "unite",
                  "frequence",
                  "disponibilite",
                  "mesures",
                  "territoires",
                  "periode_min",
                  "periode_max",
                  "statut",
                ],
                ...nodes.map(
                  (n: {
                    id: string;
                    libelle: string;
                    serviceLibelle: string;
                    categorie: string;
                    unite: string;
                    frequence: string;
                    dataAvailability: string;
                    measureCount: number;
                    territoryCount: number;
                    periodMin: string | null;
                    periodMax: string | null;
                    status: string;
                  }) => [
                    n.id,
                    n.libelle,
                    n.serviceLibelle,
                    n.categorie,
                    n.unite,
                    n.frequence,
                    n.dataAvailability,
                    String(n.measureCount),
                    String(n.territoryCount),
                    n.periodMin ?? "",
                    n.periodMax ?? "",
                    n.status,
                  ],
                ),
              ])
            }
          >
            Exporter CSV
          </Button>
        </div>
      </header>

      <div className="nbs-kpi-grid" aria-label="KPI indicateurs">
        <NbsKpiCard label="Total des indicateurs" value={summary?.totalCount} />
        <NbsKpiCard label="Indicateurs renseignés" value={summary?.withDataCount} />
        <NbsKpiCard label="Sans données" value={summary?.withoutDataCount} />
        <NbsKpiCard label="Services couverts" value={summary?.serviceCount} />
        <NbsKpiCard label="Territoires couverts" value={summary?.territoryCount} />
        <NbsKpiCard label="À valider" value={summary?.toValidateCount} />
        <NbsKpiCard
          label="Couverture"
          value={summary != null ? Number((summary.coverageRatio * 100).toFixed(1)) : undefined}
          unit="%"
          period={summary?.lastUpdatedAt ? `Maj ${summary.lastUpdatedAt}` : null}
        />
      </div>

      <NbsCatalogFilterBar
        ariaLabel="Filtres indicateurs"
        fields={filterFields}
        activeCount={activeCount}
        onReset={reset}
        trailing={
          <Button
            type="button"
            variant="secondary"
            onClick={() => setState({ view: state.view === "table" ? "cards" : "table" })}
          >
            {state.view === "table" ? "Vue cartes" : "Vue tableau"}
          </Button>
        }
      />

      {error ? (
        <Text role="alert" className="nbs-text--error">
          {error.message}{" "}
          <button type="button" className="nbs-link-button" onClick={() => void refetch()}>
            Réessayer
          </button>
        </Text>
      ) : null}

      {loading && !data ? <Text role="status">Chargement du catalogue…</Text> : null}

      {pageState === "filtered-empty" ? (
        <div className="nbs-catalog__empty">
          <Text>Aucun indicateur ne correspond à ces filtres.</Text>
          <Button type="button" variant="secondary" onClick={reset}>
            Réinitialiser les filtres
          </Button>
        </div>
      ) : null}

      {state.view === "cards" ? (
        <div className="nbs-catalog-cards">
          {nodes.map(
            (n: {
              id: string;
              libelle: string;
              serviceLibelle: string;
              unite: string;
              measureCount: number;
              dataAvailability: string;
              periodMax: string | null;
              status: string;
              territoryCount: number;
            }) => (
              <article key={n.id} className="nbs-catalog-card">
                <Heading level={3}>
                  <Link to={`/n2?indicateurId=${n.id}`}>{n.libelle}</Link>
                </Heading>
                <Text tone="muted">{n.serviceLibelle}</Text>
                <Text>
                  {n.unite} · {n.measureCount} mesure(s) · {n.territoryCount} territoire(s)
                </Text>
                <Text tone="muted">
                  {formatAvailabilityLabel(n.dataAvailability)} ·{" "}
                  {formatIndicatorStatusLabel(n.status)}
                  {n.periodMax ? ` · jusqu’à ${n.periodMax.slice(0, 4)}` : ""}
                </Text>
                <div className="nbs-catalog-card__actions">
                  <Link to={`/n2?indicateurId=${n.id}`}>Voir l’analyse</Link>
                  <Link to={`/measures?indicatorId=${n.id}`}>Voir les mesures</Link>
                </div>
              </article>
            ),
          )}
        </div>
      ) : (
        <div className="nbs-catalog-table-wrap">
          <table className="nbs-catalog-table">
            <thead>
              <tr>
                <th>Indicateur</th>
                <th>Service</th>
                <th>Catégorie</th>
                <th>Unité</th>
                <th>Fréquence</th>
                <th>Disponibilité</th>
                <th>Mesures</th>
                <th>Territoires</th>
                <th>Période</th>
                <th>Qualité</th>
                <th>Statut</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map(
                (n: {
                  id: string;
                  libelle: string;
                  serviceLibelle: string;
                  categorie: string;
                  unite: string;
                  frequence: string;
                  dataAvailability: string;
                  measureCount: number;
                  territoryCount: number;
                  periodMin: string | null;
                  periodMax: string | null;
                  quality: string | null;
                  status: string;
                }) => (
                  <tr key={n.id}>
                    <td>
                      <Link to={`/n2?indicateurId=${n.id}`}>{n.libelle}</Link>
                    </td>
                    <td>{n.serviceLibelle}</td>
                    <td>{n.categorie}</td>
                    <td>{n.unite}</td>
                    <td>{n.frequence}</td>
                    <td>
                      <span className={`nbs-status-chip nbs-status-chip--${n.dataAvailability}`}>
                        {formatAvailabilityLabel(n.dataAvailability)}
                      </span>
                    </td>
                    <td>{n.measureCount}</td>
                    <td>{n.territoryCount}</td>
                    <td>
                      {n.periodMin || n.periodMax
                        ? `${n.periodMin?.slice(0, 4) ?? "—"}–${n.periodMax?.slice(0, 4) ?? "—"}`
                        : "—"}
                    </td>
                    <td>{formatQualityLabel(n.quality)}</td>
                    <td>{formatIndicatorStatusLabel(n.status)}</td>
                    <td className="nbs-catalog-table__actions">
                      <Link to={`/n2?indicateurId=${n.id}`}>Voir l’analyse</Link>
                      <Link to={`/measures?indicatorId=${n.id}`}>Voir les mesures</Link>
                      <Link to={`/n2?indicateurId=${n.id}#provenance`}>Voir la provenance</Link>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      <footer className="nbs-catalog__pager">
        <Text tone="muted">
          {page?.totalCount ?? 0} indicateur(s) · page {state.page + 1}/{totalPages}
        </Text>
        <div className="nbs-catalog__pager-actions">
          <Button
            type="button"
            variant="secondary"
            disabled={state.page <= 0}
            onClick={() => setState({ page: state.page - 1 })}
          >
            Précédent
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={state.page + 1 >= totalPages}
            onClick={() => setState({ page: state.page + 1 })}
          >
            Suivant
          </Button>
        </div>
      </footer>
    </article>
  );
}
